import React from "react";
import axios from "axios";
import Chart from "react-apexcharts";
import ReportsError from './ReportsError';
import { 
    getGraphColours,  
    capitalizationWithExceptions
} from './Utilities';

// Class component to display the table body for the country activity report
class ReportsChannelLoginsTableBody extends React.Component {

    render() {
        const channels = this.props.channels;
        const total = this.props.total;

        return (
            <tbody key={this.props.refresh}>
                {channels.map(channel => (
                    <tr key={channel.service}>
                        <td>{channel.service}</td>
                        <td>{channel.signups}</td>
                        <td>{((channel.signups / total) * 100).toFixed(2)}%</td>
                    </tr>
                ))}
            </tbody>
        );   
    }
}

class ReportsChannelLoginsChart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hiddenBars: new Set()
        };
    }

    handleLegendClick = (categoryIndex) => {
        this.setState((prevState) => {
            const hiddenBars = new Set(prevState.hiddenBars);
            if (hiddenBars.has(categoryIndex)) {
                hiddenBars.delete(categoryIndex); // Re-enable bar
            } else {
                hiddenBars.add(categoryIndex); // Disable bar
            }
            return { hiddenBars };
        });
    };

    render() {
        let services = [];
        let counts = [];

        this.props.channels.forEach((channel, index) => {
            if (channel.service && !isNaN(channel.signups)) {
                let cleanedService = channel.service.replace(/\s+$/g, '');
                services.push(capitalizationWithExceptions(cleanedService));
                counts.push(parseInt(channel.signups, 10));
            }
        });

        let adjustedCounts = counts.map((count, index) =>
            this.state.hiddenBars.has(index) ? 0 : count
        );

        // Single subtle greyish blue color for both bars and legend
        const unifiedColor = "#6A85B6";

        const params = {
            options: {
                chart: {
                    id: "report-bar-chart",
                    type: "bar",
                    stacked: false
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: "80%"
                    }
                },
                xaxis: {
                    categories: services,
                    title: {
                        text: ""
                    }
                },
                yaxis: {
                    title: {
                        text: "Signups"
                    }
                },
                tooltip: {
                    y: {
                        formatter: function (value, { dataPointIndex }) {
                            return value === 0
                                ? "Click legend to restore"
                                : `${counts[dataPointIndex]} signups`;
                        }
                    }
                },
                colors: [unifiedColor], // ✅ Ensure all bars use the same greyish blue
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false // Disable built-in legend
                }
            },
            series: [
                {
                    name: "Signups",
                    data: adjustedCounts
                }
            ]
        };

        return (
            <div style={{ width: "100%", textAlign: "center" }}>
                {/* Custom Legend */}
                <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", marginBottom: "10px" }}>
                    {services.map((service, index) => (
                        <div
                            key={index}
                            onClick={() => this.handleLegendClick(index)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                margin: "5px 10px",
                                opacity: this.state.hiddenBars.has(index) ? 0.5 : 1
                            }}
                        >
                            <div style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: this.state.hiddenBars.has(index) ? "#ccc" : unifiedColor,
                                marginRight: "5px",
                                border: "1px solid #666"
                            }}></div>
                            <span>{service}</span>
                        </div>
                    ))}
                </div>

                {/* Chart */}
                <Chart
                    options={params.options}
                    series={params.series}
                    type="bar"
                    width="100%"
                    height="400"
                    key={this.props.refresh}
                />
            </div>
        );
    }
}

// Main component to display the country activity report
class ReportsChannelLogins extends React.Component {
    constructor(props) {
        super(props);

        var endpoint = "login/stats/channels?turnoffdebug=1";

        // Setting initial state
        this.state = {
            total: null,
            view: props.view ? props.view : "table",
            url: "react/process?endpoint=" + endpoint + "&hourly_cutoff=0",
            channels: {},
            isLoading: true,
            error: null
        };
    }

    componentDidMount() {
        this.getData();
    }

    // Function to fetch data
    getData() {
        axios.get(this.state.url).then(response => this.formatData(response.data));
    }

    // Function to format the fetched data
    formatData(data) {
        console.log("Raw data received:", data);
        let channels = data.channels || [];
        let total = data.total || 0;
        let error = data?.error || null;

        // Sorting countries based on signups
        channels = channels.sort((a, b) => b.signups - a.signups);

        console.log("Processed channels:", channels);
        console.log("Total signups:", total);

        this.setState({ channels, total, error: error?.message, isLoading: false });
    }


    // Function to set the total count of entries
    setTotalCount = (total) => {
        total = total.replace("~", "").replace(",", "");
        this.setState({ total: parseInt(total, 10) });
    };

    render() {
        const { isLoading, channels } = this.state;

        if (isLoading) {
            return (
                <div className="row">
                    <div className="col-md-4"></div>
                    <div className="loading col-md-4">
                        <div className="react-loading big" style={this.props.loaderStyle}></div>
                    </div>
                    <div className="col-md-4"></div>
                </div>
            );
        } else if (!isLoading && Object.keys(channels).length === 0) {
            // Handling no results found
            return (
                <ReportsError error={this.state.error} table="false" type="activities" />
            );
        } else {
            if (this.state.view === "graph") return this.renderGraph();
            return this.renderTable();
        }
    }

    renderTable(){    
        return (
            <div className="feed-channellogins">
                <table id="basic-datatable" className="table dt-responsive nowrap w-100">
                    <thead>
                        <tr>
                            <th>Channel</th>
                            <th>Sign Ins</th>
                            <th>Percentage</th>
                        </tr>
                    </thead>
                    <ReportsChannelLoginsTableBody
                        channels={this.state.channels}
                        total={this.state.total}
                        refresh={this.state.refresh}
                    />
                </table>
            </div>
        );
    }

    renderGraph(){
        return(
            <div className="chart-channellogins">
                <ReportsChannelLoginsChart 
                    channels={this.state.channels}
                    total={this.state.total}
                    refresh={this.state.refresh}
                />
            </div>
        );
    }
}

export default ReportsChannelLogins;
